import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Header from 'components/ui/Header';
import Footer from 'components/ui/Footer';
import FloatingActionButtons from 'components/ui/FloatingActionButtons';
import Image from 'components/AppImage';
import Button from 'components/ui/Button';
import Icon from 'components/AppIcon';

const PackageDetails = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [selectedImage, setSelectedImage] = useState(0);
  const [selectedPackage, setSelectedPackage] = useState('standard');
  const [showLightbox, setShowLightbox] = useState(false);
  const [expandedFaq, setExpandedFaq] = useState(null);

  const packageData = location?.state?.selectedResort || location?.state?.selectedItem || {
    id: 1,
    name: "Dandeli Adventure Package",
    category: "adventure",
    images: [
      "https://images.unsplash.com/photo-1551632811-561732d1e306",
      "https://images.unsplash.com/photo-1544551763-46a013bb70d5",
      "https://images.unsplash.com/photo-1549366021-9f761d040a94",
      "https://images.unsplash.com/photo-1566073771259-6a8506099945",
      "https://images.unsplash.com/photo-1571896349842-33c89424de2d"
    ],
    rating: 4.8,
    reviews: 324
  };

  const packages = {
    standard: {
      name: "Standard Adventure Package",
      duration: "2 Days / 1 Night", 
      price: "₹2,999",
      originalPrice: "₹3,999",
      discount: "25% OFF",
      groupSize: "2-6 People",
      includes: [
        "1 Night Accommodation (Deluxe Room)",
        "All Meals (Breakfast, Lunch, Dinner)",
        "White Water Rafting",
        "Jungle Safari",
        "Nature Walk with Guide",
        "Campfire Evening",
        "Transportation within Resort",
        "First Aid & Safety Equipment"
      ],
      excludes: [
        "Transportation to/from Resort",
        "Personal Expenses",
        "Additional Adventure Activities",
        "Alcoholic Beverages",
        "Travel Insurance",
        "Tips and Gratuities"
      ]
    },
    premium: {
      name: "Premium Adventure Package",
      duration: "3 Days / 2 Nights",
      price: "₹4,999", 
      originalPrice: "₹6,499",
      discount: "23% OFF",
      groupSize: "2-4 People",
      includes: [
        "2 Nights Accommodation (Premium River View)",
        "All Meals + Evening Snacks",
        "White Water Rafting (Advanced)",
        "Jungle Safari (2 Sessions)",
        "Rock Climbing & Rappelling",
        "Kayaking Experience",
        "Bird Watching Tour",
        "Spa Session (1 Hour)",
        "Campfire with BBQ",
        "Professional Photography",
        "Airport/Station Pickup",
        "Travel Insurance"
      ],
      excludes: [
        "Airfare/Train Tickets",
        "Personal Shopping",
        "Extra Spa Treatments",
        "Alcoholic Beverages",
        "Room Service",
        "Late Checkout Charges"
      ]
    },
    luxury: {
      name: "Luxury Complete Experience",
      duration: "4 Days / 3 Nights",
      price: "₹8,999",
      originalPrice: "₹11,999", 
      discount: "25% OFF",
      groupSize: "2 People",
      includes: [
        "3 Nights Luxury Suite (Riverside)",
        "All Meals + Room Service",
        "All Adventure Activities",
        "Private Guide & Vehicle",
        "Helicopter Ride (Subject to Weather)",
        "Luxury Spa Package",
        "Private Dining Experience",
        "Wildlife Photography Workshop",
        "Sunset River Cruise",
        "Cultural Performance Evening",
        "Complimentary Airport Transfer",
        "Personal Butler Service",
        "Travel & Activity Insurance"
      ],
      excludes: [
        "Airfare",
        "Personal Shopping & Souvenirs",
        "Alcoholic Beverages (Premium Brands)",
        "Extra Helicopter Rides",
        "Medical Expenses"
      ]
    }
  };

  const testimonials = [
    {
      id: 1,
      name: "Rajesh & Priya Sharma",
      location: "Bangalore",
      photo: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d",
      rating: 5,
      review: "The premium package exceeded our expectations! Every activity was perfectly organized, and the accommodation was luxurious. The guides were knowledgeable and safety was never compromised.",
      package: "Premium Adventure Package"
    },
    {
      id: 2,
      name: "Meera Patel",
      location: "Mumbai",
      photo: "https://images.unsplash.com/photo-1494790108755-2616b612b47c", 
      rating: 5,
      review: "Perfect family adventure! The standard package had everything we needed. Kids loved the jungle safari and the evening campfire was magical. Great value for money!",
      package: "Standard Adventure Package"
    }
  ];

  const faqs = [
    {
      question: "What\'s the best time to visit Dandeli?",
      answer: "The best time to visit Dandeli is from October to May when the weather is pleasant for outdoor activities. Monsoon season (June-September) offers lush greenery but some water activities may be restricted."
    },
    {
      question: "Are the adventure activities safe for beginners?",
      answer: "Yes, absolutely! All our activities are conducted by certified professionals with proper safety equipment. We provide comprehensive briefings and training for beginners before each activity."
    },
    {
      question: "Can I customize the package according to my preferences?",
      answer: "Yes, we offer flexible customization options. You can add or remove activities, extend your stay, or upgrade accommodations. Contact our team to create a personalized package."
    },
    {
      question: "What should I bring for the adventure activities?",
      answer: "We provide all necessary safety equipment. You should bring comfortable clothes, sports shoes, sun protection, personal medications, and a camera. Detailed packing list will be shared upon booking."
    },
    {
      question: "Is transportation included in the packages?",
      answer: "Transportation inclusion varies by package. Standard includes resort transportation, Premium includes pickup/drop from nearby stations, and Luxury includes airport transfers. Check specific package details."
    }
  ];

  const currentPackage = packages?.[selectedPackage];

  const handleBookNow = () => {
    navigate('/booking-engine', { 
      state: { 
        selectedPackage: {
          ...packageData,
          packageDetails: currentPackage
        }
      }
    });
  };

  const handleWhatsAppBooking = () => {
    const message = encodeURIComponent(`Hi! I'm interested in the ${currentPackage?.name} for ${packageData?.name}. Can you help me with booking and provide more details about availability?`);
    window.open(`https://wa.me/918277385225?text=${message}`, '_blank');
  };

  const renderStars = (rating) => {
    return [...Array(5)]?.map((_, index) => (
      <Icon
        key={index}
        name="Star"
        size={16}
        className={index < Math.floor(rating) ? 'text-warning' : 'text-gray-300'}
      />
    ));
  };

  const toggleFaq = (index) => {
    setExpandedFaq(expandedFaq === index ? null : index);
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <Header />
      {/* Hero Section with Gallery */}
      <section className="pt-16">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Image Gallery */}
            <div className="space-y-4">
              {/* Main Image */}
              <div className="relative h-96 rounded-2xl overflow-hidden">
                <Image
                  src={packageData?.images?.[selectedImage]}
                  alt={`${packageData?.name} - Image ${selectedImage + 1}`}
                  className="w-full h-full object-cover cursor-pointer hover:scale-105 transition-organic duration-500"
                  onClick={() => setShowLightbox(true)}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent"></div>
                <button
                  onClick={() => setShowLightbox(true)}
                  className="absolute top-4 right-4 bg-white/20 backdrop-blur-sm text-white p-2 rounded-full hover:bg-white/30 transition-organic"
                >
                  <Icon name="Maximize" size={20} />
                </button>
              </div>

              {/* Thumbnail Gallery */}
              <div className="grid grid-cols-5 gap-2">
                {packageData?.images?.map((image, index) => (
                  <button
                    key={index}
                    onClick={() => setSelectedImage(index)}
                    className={`h-20 rounded-lg overflow-hidden border-2 transition-organic ${
                      selectedImage === index ? 'border-primary' : 'border-transparent hover:border-primary/50'
                    }`}
                  >
                    <Image
                      src={image}
                      alt={`Thumbnail ${index + 1}`}
                      className="w-full h-full object-cover"
                    />
                  </button>
                ))}
              </div>
            </div>

            {/* Package Info */}
            <div className="space-y-6">
              {/* Header */}
              <div>
                <h1 className="text-3xl md:text-4xl font-heading font-bold text-text-primary mb-2">
                  {packageData?.name}
                </h1>
                <div className="flex items-center space-x-4 text-text-secondary">
                  <div className="flex items-center">
                    {renderStars(packageData?.rating || 4.8)}
                    <span className="ml-2 font-body text-sm">
                      {packageData?.rating || 4.8} ({packageData?.reviews || 324} reviews)
                    </span>
                  </div>
                  <div className="flex items-center">
                    <Icon name="MapPin" size={16} className="mr-1" />
                    <span className="font-caption text-sm">Dandeli, Karnataka</span>
                  </div>
                </div>
              </div>

              {/* Package Selection Tabs */}
              <div className="border border-border rounded-lg p-1 bg-muted">
                <div className="grid grid-cols-3 gap-1">
                  {Object.entries(packages)?.map(([key, pkg]) => (
                    <button
                      key={key}
                      onClick={() => setSelectedPackage(key)}
                      className={`px-4 py-3 rounded text-sm font-heading font-medium transition-organic ${
                        selectedPackage === key
                          ? 'bg-primary text-primary-foreground shadow-sm'
                          : 'text-text-secondary hover:text-text-primary hover:bg-background'
                      }`}
                    >
                      {pkg?.name?.split(' ')?.[0]}
                    </button>
                  ))}
                </div>
              </div>

              {/* Current Package Details */}
              <div className="bg-card rounded-2xl p-6 shadow-organic">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-2xl font-heading font-bold text-text-primary">
                    {currentPackage?.name}
                  </h2>
                  <div className="bg-error text-error-foreground px-3 py-1 rounded-full text-sm font-heading font-bold">
                    {currentPackage?.discount}
                  </div>
                </div>

                {/* Package Stats */}
                <div className="grid grid-cols-2 gap-4 mb-6">
                  <div className="flex items-center">
                    <Icon name="Clock" size={18} className="text-primary mr-2" />
                    <div>
                      <p className="text-sm text-text-secondary font-caption">Duration</p>
                      <p className="font-heading font-semibold text-text-primary">
                        {currentPackage?.duration}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center">
                    <Icon name="Users" size={18} className="text-primary mr-2" />
                    <div>
                      <p className="text-sm text-text-secondary font-caption">Group Size</p>
                      <p className="font-heading font-semibold text-text-primary">
                        {currentPackage?.groupSize}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Pricing */}
                <div className="flex items-baseline space-x-3 mb-6">
                  <span className="text-3xl font-heading font-bold text-primary">
                    {currentPackage?.price}
                  </span>
                  <span className="text-xl text-text-secondary line-through font-body">
                    {currentPackage?.originalPrice}
                  </span>
                  <span className="text-sm text-text-secondary font-caption">per person</span>
                </div>

                {/* Action Buttons */}
                <div className="space-y-3">
                  <Button
                    variant="default"
                    size="lg"
                    iconName="Calendar"
                    iconPosition="left"
                    fullWidth
                    onClick={handleBookNow}
                    className="hover:scale-105 transition-organic"
                  >
                    Book This Package
                  </Button>
                  <Button
                    variant="outline"
                    size="lg"
                    iconName="MessageCircle"
                    iconPosition="left"
                    fullWidth
                    onClick={handleWhatsAppBooking}
                    className="border-green-600 text-green-600 hover:bg-green-600 hover:text-white"
                  >
                    Customize on WhatsApp
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* Package Details Sections */}
      <section className="py-16 bg-muted">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Inclusions */}
            <div className="bg-card rounded-2xl p-6 shadow-organic">
              <h3 className="text-xl font-heading font-semibold text-text-primary mb-4 flex items-center">
                <Icon name="Check" size={20} className="text-success mr-2" />
                What's Included
              </h3>
              <ul className="space-y-3">
                {currentPackage?.includes?.map((item, index) => (
                  <li key={index} className="flex items-start text-sm">
                    <Icon name="Check" size={14} className="text-success mr-2 mt-0.5 flex-shrink-0" />
                    <span className="text-text-secondary font-body">{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Exclusions */}
            <div className="bg-card rounded-2xl p-6 shadow-organic">
              <h3 className="text-xl font-heading font-semibold text-text-primary mb-4 flex items-center">
                <Icon name="X" size={20} className="text-error mr-2" />
                Not Included
              </h3>
              <ul className="space-y-3">
                {currentPackage?.excludes?.map((item, index) => (
                  <li key={index} className="flex items-start text-sm">
                    <Icon name="X" size={14} className="text-error mr-2 mt-0.5 flex-shrink-0" />
                    <span className="text-text-secondary font-body">{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Important Info */}
            <div className="bg-card rounded-2xl p-6 shadow-organic">
              <h3 className="text-xl font-heading font-semibold text-text-primary mb-4 flex items-center">
                <Icon name="Info" size={20} className="text-primary mr-2" />
                Important Information
              </h3>
              <div className="space-y-4 text-sm">
                <div>
                  <h4 className="font-heading font-semibold text-text-primary mb-2">Cancellation Policy</h4>
                  <p className="text-text-secondary font-body">
                    Free cancellation up to 48 hours before check-in. 50% refund for 24-48 hours notice.
                  </p>
                </div>
                <div>
                  <h4 className="font-heading font-semibold text-text-primary mb-2">Age Restrictions</h4>
                  <p className="text-text-secondary font-body">
                    Some activities have age restrictions (6+ for rafting, 12+ for rock climbing).
                  </p>
                </div>
                <div>
                  <h4 className="font-heading font-semibold text-text-primary mb-2">Weather Dependency</h4>
                  <p className="text-text-secondary font-body">
                    Some activities are weather-dependent. Alternative indoor activities provided.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* Customer Testimonials */}
      <section className="py-16 bg-background">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-text-primary mb-4">
              What Our Guests Say
            </h2>
            <p className="text-lg text-text-secondary max-w-2xl mx-auto font-body">
              Real experiences from guests who have enjoyed our packages
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {testimonials?.map((testimonial) => (
              <div key={testimonial?.id} className="bg-card rounded-2xl p-6 shadow-organic">
                <div className="flex items-start space-x-4 mb-4">
                  <Image
                    src={testimonial?.photo}
                    alt={testimonial?.name}
                    className="w-16 h-16 rounded-full object-cover"
                  />
                  <div className="flex-1">
                    <h4 className="font-heading font-semibold text-text-primary">
                      {testimonial?.name}
                    </h4>
                    <p className="text-sm text-text-secondary font-caption">
                      {testimonial?.location} • {testimonial?.package}
                    </p>
                    <div className="flex items-center mt-1">
                      {renderStars(testimonial?.rating)}
                    </div>
                  </div>
                </div>
                <blockquote className="text-text-primary font-body italic">
                  "{testimonial?.review}"
                </blockquote>
              </div>
            ))}
          </div>
        </div>
      </section>
      {/* FAQ Section */}
      <section className="py-16 bg-muted">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-text-primary mb-4">
              Frequently Asked Questions
            </h2>
            <p className="text-lg text-text-secondary font-body">
              Get answers to common questions about our packages
            </p>
          </div>

          <div className="space-y-4">
            {faqs?.map((faq, index) => (
              <div key={index} className="bg-card rounded-lg shadow-organic overflow-hidden">
                <button
                  onClick={() => toggleFaq(index)}
                  className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-muted transition-organic"
                >
                  <h3 className="font-heading font-semibold text-text-primary pr-4">
                    {faq?.question}
                  </h3>
                  <Icon
                    name={expandedFaq === index ? "ChevronUp" : "ChevronDown"}
                    size={20}
                    className="text-text-secondary flex-shrink-0"
                  />
                </button>
                {expandedFaq === index && (
                  <div className="px-6 pb-4">
                    <p className="text-text-secondary font-body">
                      {faq?.answer}
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>
      {/* Sticky Booking Bar - Mobile */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-border p-4 lg:hidden z-40">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs text-text-secondary font-caption">Starting from</p>
            <p className="text-lg font-heading font-bold text-primary">
              {currentPackage?.price}
              <span className="text-sm text-text-secondary font-caption ml-1">per person</span>
            </p>
          </div>
          <div className="flex space-x-2">
            <Button
              variant="outline"
              size="sm"
              iconName="MessageCircle"
              onClick={handleWhatsAppBooking}
              className="border-green-600 text-green-600 hover:bg-green-600 hover:text-white"
            >
              WhatsApp
            </Button>
            <Button
              variant="default"
              size="sm"
              iconName="Calendar"
              onClick={handleBookNow}
            >
              Book Now
            </Button>
          </div>
        </div>
      </div>
      {/* Footer */}
      <Footer />
      {/* Floating Action Buttons */}
      <FloatingActionButtons />
    </div>
  );
};

export default PackageDetails;